import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import { loadVaultList, type VaultFlowDeps, type VaultListResult } from '../services/vaultFlow'
import {
  analyzeInactive2fa,
  analyzeReused,
  analyzeUnsecured,
  analyzeWeak,
  type ReportItem,
} from '../services/reportAnalysis'

/**
 * セキュリティレポートのデータ取得 state machine (T-D-Reports)。
 *
 * 設計判断:
 *   - useHomeData と同じ pattern — loadVaultList を 1 度呼び、分析関数を適用して結果を返す。
 *   - すべてクライアント側分析のみ。外部 API / ドメインへの通信は行わない (ゼロ知識方針)。
 *   - `loadFn` DI でネットワークをバイパスしてテスト可能。
 */

export interface ReportsResult {
  readonly totalItems: number
  readonly reused: readonly ReportItem[]
  readonly weak: readonly ReportItem[]
  readonly unsecured: readonly ReportItem[]
  readonly inactive2fa: readonly ReportItem[]
}

export type ReportsDataState =
  | { kind: 'loading' }
  | { kind: 'ready'; result: ReportsResult; failureCount: number }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS = {
  no_session: 'reports.error.no_session',
  network: 'reports.error.network',
  generic: 'reports.error.generic',
} as const

const messageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

export interface UseReportsDataOptions {
  flowDeps?: VaultFlowDeps
  loadFn?: (sk: Uint8Array) => Promise<VaultListResult>
}

export const useReportsData = (options: UseReportsDataOptions = {}): ReportsDataState => {
  const { loadFn, flowDeps } = options
  const [state, setState] = useState<ReportsDataState>({ kind: 'loading' })
  const mountedRef = useRef(true)

  const fetch = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) {
        setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })
      }

      return
    }

    try {
      const vaultResult = loadFn
        ? await loadFn(session.symmetricKey)
        : await loadVaultList(session.symmetricKey, flowDeps)

      if (!mountedRef.current) return

      const items = vaultResult.items

      setState({
        kind: 'ready',
        result: {
          totalItems: items.length,
          reused: analyzeReused(items),
          weak: analyzeWeak(items),
          unsecured: analyzeUnsecured(items),
          inactive2fa: analyzeInactive2fa(items),
        },
        failureCount: vaultResult.failures.length,
      })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    }
  }, [loadFn, flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetch()

    return () => {
      mountedRef.current = false
    }
  }, [fetch])

  return useMemo(() => state, [state])
}
